import { createSlice } from "@reduxjs/toolkit"
import { globalKeys } from "../../utils/tools";
import { cancelDeviceSubsriptionThunk, createDeviceThunk, deleteDeviceThunk, deviceListThunk, devicePaymentThunk, deviceSubscribeThunk, getDeviceDetailThunk, regenerateClaimCodeThunk, updateDeviceThunk } from "./deviceThunks";

const initialState = {
    status: "initial", // initial, loading, error, loaded
    message: "",
    event: "",
    data: {},
    // Claim-code state is isolated from `status`/`message` so regenerating a
    // pairing code from the device-list page doesn't trample the list-loaded
    // render condition. Set by createDeviceThunk and regenerateClaimCodeThunk.
    claim: null,
    claimStatus: "idle", // idle, loading, success, error
    claimMessage: "",
}

export const deviceSlice = createSlice({
    name: "device",
    initialState,
    reducers: {
        resetDeviceState: (state) => {
            state.status = "initial"; // initial, loading, error, loade;
            state.message = "";
            state.event = "";
            state.data = {};
            state.claim = null;
            state.claimStatus = "idle";
            state.claimMessage = "";
        }
    },
    extraReducers: (builder) => {
        // registration
        builder.addCase(deviceListThunk.pending, (state) => {
            state.event = 'list';
            state.status = "loading";
        }).addCase(deviceListThunk.fulfilled, (state, action) => {
            state.event = 'list';
            var response = action.payload;
            state.data = response;
            state.message = 'Success';
            state.status = "loaded";
        }).addCase(deviceListThunk.rejected, (state, action) => {
            state.event = 'list';
            state.status = "error";
            state.message = action.error?.message;
        })
        // get current user

        builder.addCase(updateDeviceThunk.pending, (state) => {
            state.event = 'update';
            state.status = "loading";
        }).addCase(updateDeviceThunk.fulfilled, (state, action) => {
            var response = action.payload;
            state.data = response.device;
            state.message = 'Success';
            state.event = 'update';
            state.status = "update_success";
        }).addCase(updateDeviceThunk.rejected, (state, action) => {
            state.event = 'update';
            state.status = "error";
            state.message = action.error?.message;
        })

        builder.addCase(createDeviceThunk.pending, (state) => {
            state.event = "create_device"
            state.status = "loading";
            state.claim = null;
        }).addCase(createDeviceThunk.fulfilled, (state, { payload }) => {
            state.event = "create_device"

            if (!payload?.device) {
                state.status = "error";
                state.message = payload?.message ?? "Device Id already exists"
                state.claim = null;
            } else {
                state.status = "success";
                state.message = payload?.message
                state.data = payload?.device;
                state.claim = payload?.claim ?? null;
            }
        }).addCase(createDeviceThunk.rejected, (state, action) => {
            state.event = "create_device"
            state.status = "error";
            state.message = action.error?.message;
            state.claim = null;
        })

        builder.addCase(getDeviceDetailThunk.pending, (state) => {
            state.event = "detail_device"
            state.status = "loading";
        }).addCase(getDeviceDetailThunk.fulfilled, (state, { payload }) => {
            state.event = "detail_device"

            if (payload && payload.length <= 0) {
                state.status = "error";
                state.message = payload?.message ?? "No device found"
            } else {
                state.status = "loaded";
                state.message = payload?.message
                state.data = payload;
            }
        }).addCase(getDeviceDetailThunk.rejected, (state, action) => {
            state.event = "detail_device"
            state.status = "error";
            state.message = action.error?.message;
        })

        builder.addCase(devicePaymentThunk.pending, (state) => {
            state.event = "device_payment"
            state.status = "loading";
        }).addCase(devicePaymentThunk.fulfilled, (state, { payload }) => {
            state.event = "device_payment"

            if (payload && payload.length <= 0) {
                state.status = "error";
                state.message = payload?.message ?? "No device found"
            } else {
                state.status = "success_paymemt";
                state.message = payload?.message
                state.data = payload;
            }
        }).addCase(devicePaymentThunk.rejected, (state, action) => {
            state.event = "device_payment"
            state.status = "error";
            state.message = action.error?.message;
        })
        
        builder.addCase(deviceSubscribeThunk.pending, (state) => {
            state.event = 'device_subsribe';
            state.status = "loading";
        }).addCase(deviceSubscribeThunk.fulfilled, (state, action) => {
            state.event = 'device_subsribe';
            var response = action.payload;
            if (!(response?.id)) {
                state.status = "error";
                state.data = null;
                state.message = response?.message ?? "Token not found. Please contact administrator";
            } else {
                state.status = "success";
                // state.message = response?.message;
                // state.data = response?.device
            }
        }).addCase(deviceSubscribeThunk.rejected, (state, action) => {
            state.event = 'device_subsribe';
            state.status = "error";
            state.message = action.error?.message;
        })

        
        // Note: regenerate flow only touches claim* fields, never `status`/`message`,
        // so the parent page (e.g. /devices) keeps its list rendered while the
        // modal opens.
        builder.addCase(regenerateClaimCodeThunk.pending, (state) => {
            state.claimStatus = "loading";
            state.claim = null;
            state.claimMessage = "";
        }).addCase(regenerateClaimCodeThunk.fulfilled, (state, { payload }) => {
            if (payload?.claim?.code) {
                state.claimStatus = "success";
                state.claim = payload.claim;
                state.claimMessage = "";
            } else {
                state.claimStatus = "error";
                state.claimMessage = payload?.message ?? "Could not generate pairing code";
                state.claim = null;
            }
        }).addCase(regenerateClaimCodeThunk.rejected, (state, action) => {
            state.claimStatus = "error";
            state.claimMessage = action.error?.message;
            state.claim = null;
        })

        builder.addCase(deleteDeviceThunk.pending, (state) => {
            state.event = 'delete';
            state.status = "loading";
            state.message = "";
        }).addCase(deleteDeviceThunk.fulfilled, (state, { payload }) => {
            state.event = 'delete';
            if (payload && payload.device_id) {
                state.status = "delete_success";
                state.message = payload.message || "Device deleted";
            } else {
                state.status = "error";
                state.message = payload?.message ?? "Could not delete device";
            }
        }).addCase(deleteDeviceThunk.rejected, (state, action) => {
            state.event = 'delete';
            state.status = "error";
            state.message = action.error?.message;
        })

        builder.addCase(cancelDeviceSubsriptionThunk.pending, (state) => {
            state.event = 'cancel_subsribe';
            state.status = "loading";
        }).addCase(cancelDeviceSubsriptionThunk.fulfilled, (state, action) => {
            state.event = 'cancel_subsribe';
            var response = action.payload;
            if (response && response?.error) {
                state.status = "error";
                state.data = null;
                state.message = response?.error ?? "Token not found. Please contact administrator";
            } else {
                state.status = "success";
                state.message = response?.message;
                // state.data = response?.device
            }
        }).addCase(cancelDeviceSubsriptionThunk.rejected, (state, action) => {
            state.event = 'cancel_subsribe';
            state.status = "error";
            state.message = action.error?.message;
        })
    }
});

export const { resetDeviceState } = deviceSlice.actions;

export const selectDevice = (state) => state.device;

export default deviceSlice.reducer;